"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { useJobListStore } from "@/features/jobs/store/job-store";
import { Toolbar } from "@/components/ui/toolbar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import {
  FilterButton,
  type ActiveFilter,
  type FilterFieldDef,
  type SearchableFilterOption,
} from "@/components/ui/filter-panel";
import { useListMembers } from "@/lib/api/generated";
import useIsMobile from "@/hooks/use-is-mobile";

const STATUS_FIELD: FilterFieldDef = {
  key: "status",
  label: "Status",
  icon: "IconCircleCheck",
  options: [
    { value: "active", label: "Active", badgeVariant: "success", badgeClassName: "border-border" },
    { value: "draft", label: "Draft", badgeVariant: "warning", badgeClassName: "border-border" },
    { value: "closed", label: "Closed", badgeVariant: "outline-muted", badgeClassName: "border-border bg-contrast/4 text-contrast/50" },
  ],
};

/* ── Skeleton ─────────────────────────────────────────────────── */

export function JobListFiltersSkeleton() {
  return (
    <Toolbar.Container className="px-4 py-3.5 border-b border-border">
      <div className="w-[200px] h-7 bg-contrast/3 rounded-md shimmer shimmer-bg text-foreground" />
      <Toolbar.Separator />
      <div className="w-40 h-7 bg-contrast/3 rounded-md shimmer shimmer-bg text-foreground" />
      <Toolbar.Separator />
      <div className="w-20 h-7 bg-contrast/3 rounded-md shimmer shimmer-bg text-foreground" />
    </Toolbar.Container>
  );
}

/* ── Main component ───────────────────────────────────────────── */

export type JobsScope = "my" | "all";

interface JobListFiltersProps {
  jobsScope: JobsScope;
  onJobsScopeChange: (scope: JobsScope) => void;
}

export function JobListFilters({
  jobsScope,
  onJobsScopeChange,
}: JobListFiltersProps) {
  const {
    search,
    listFilters,
    setSearch,
    setListFilters,
  } = useJobListStore();
  const isMobile = useIsMobile();
  const { data: membersData } = useListMembers(undefined);

  const [filterOpen, setFilterOpen] = useState(false);

  const filterFields = useMemo(() => {
    const members = membersData?.members ?? [];
    const searchableOptions: SearchableFilterOption[] = members.map((m) => ({
      id: m.id,
      label: `${m.first_name} ${m.last_name}`.trim() || m.email,
      avatarUrl: m.avatar_url ?? null,
    }));
    const managerField: FilterFieldDef = {
      key: "manager",
      label: "Manager",
      icon: "IconUser",
      type: "searchable",
      options: [],
      searchableOptions,
      placeholder: "Select manager…",
    };
    return jobsScope === "my" ? [STATUS_FIELD] : [STATUS_FIELD, managerField];
  }, [jobsScope, membersData?.members]);

  const activeFilters = useMemo(() => {
    if (jobsScope === "my") {
      return listFilters.filter((filter) => filter.field !== "manager");
    }
    return listFilters;
  }, [jobsScope, listFilters]);

  const handleFiltersChange = (filters: ActiveFilter[]) => {
    if (jobsScope === "my") {
      setListFilters(filters.filter((filter) => filter.field !== "manager"));
      return;
    }
    setListFilters(filters);
  };

  if (isMobile) {
    return (
      <div className="border-b border-border">
        <div className="flex items-center px-page py-2 border-b border-border">
          <Tabs
            value={jobsScope}
            onValueChange={(v) => onJobsScopeChange(v as JobsScope)}
          >
            <TabsList>
              <TabsTrigger value="all">All Jobs</TabsTrigger>
              <TabsTrigger value="my">My Jobs</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="flex items-center gap-3 px-page py-2">
          <Toolbar.Search
            value={search}
            onChange={setSearch}
            className="flex-1"
          />
          <Toolbar.Separator />
          <FilterButton
            fields={filterFields}
            filters={activeFilters}
            onFiltersChange={handleFiltersChange}
            open={filterOpen}
            onOpenChange={setFilterOpen}
          />
        </div>
      </div>
    );
  }

  return (
    <Toolbar.Container data-tour="jobs-filters">
      <Tabs
        value={jobsScope}
        onValueChange={(v) => onJobsScopeChange(v as JobsScope)}
      >
        <TabsList>
          <TabsTrigger value="all">All Jobs</TabsTrigger>
          <TabsTrigger value="my">My Jobs</TabsTrigger>
        </TabsList>
      </Tabs>

      <Toolbar.Separator />

      <Toolbar.Search value={search} onChange={setSearch} className="w-40" />

      <Toolbar.Separator />

      <FilterButton
        fields={filterFields}
        filters={activeFilters}
        onFiltersChange={handleFiltersChange}
        open={filterOpen}
        onOpenChange={setFilterOpen}
      />

      <div className="flex-1 2xl:hidden" />

      <Button
        asChild
        variant="secondary"
        size="sm"
        className="2xl:hidden shrink-0"
        icon={<Icon name="IconPuzzle" size={15} />}
      >
        <Link href="/settings/integrations">Connect ATS</Link>
      </Button>
    </Toolbar.Container>
  );
}
